import React, { useState, useEffect } from 'react';
import { Row, Col, Button, Input, InputNumber, Select, message, Space, List, Upload, Progress } from 'antd';
import { 
  VideoCameraOutlined,  
  FileTextOutlined,  
  PlayCircleOutlined,  
  SaveOutlined,
  DeleteOutlined,
  UploadOutlined,
  LoadingOutlined,
  CheckCircleOutlined
} from '@ant-design/icons';
import axios from 'axios';

const { Option } = Select;

const MobileVideoEditor = () => {
  const [files, setFiles] = useState([]); 
  const [activeRenders, setActiveRenders] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedVideo, setSelectedVideo] = useState(null); 
  const [multiQueue, setMultiQueue] = useState([]); 
  const [renderMode, setRenderMode] = useState('single'); 
  const [exportName, setExportName] = useState('');
  const [resW, setResW] = useState(1280);
  const [resH, setResH] = useState(720);
  const [bitrate, setBitrate] = useState('2000');
  const [fps, setFps] = useState(30);
  const [startH, setStartH] = useState(0);
  const [startM, setStartM] = useState(0);
  const [startS, setStartS] = useState(0);
  const [duration, setDuration] = useState(10);
  const [cropW, setCropW] = useState(null);
  const [cropH, setCropH] = useState(null);
  const [cropX, setCropX] = useState(0);
  const [cropY, setCropY] = useState(0);
  const [stackCount, setStackCount] = useState(1); 
  const [processing, setProcessing] = useState(false);

  // Funkcija koja podiže polje iznad tastature
  const handleFocus = (e) => {
    setTimeout(() => {
      e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 300);
  };

  const fetchData = async () => {
    try {
      const response = await axios.get('/api/box/media/');
      const allMedia = response.data.filter(f => f.file_type === 'video' || f.media_type === 'video');
      const ready = allMedia.filter(f => f.duration > 0);
      const inProgress = allMedia.filter(f => !f.duration || f.duration === 0);
      setFiles(ready);
      setActiveRenders(inProgress);
    } catch (error) { console.error('Fetch error'); }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 4000);
    return () => clearInterval(interval);
  }, []);

  const handleUpload = async (options) => {
    const { file, onSuccess, onError } = options;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', file.name);
    formData.append('file_type', 'video');
    formData.append('file_size', file.size);
    setIsUploading(true);
    setUploadProgress(0);
    try {
      const response = await axios.post('/api/box/media/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => setUploadProgress(Math.round((e.loaded * 100) / e.total))
      });
      message.success('Uploaded!');
      setIsUploading(false);
      onSuccess(response.data);
      fetchData();
    } catch (error) {
      setIsUploading(false);
      onError(error);
      message.error('Upload failed');
    }
  };

  const handleSelectMedia = (video) => {
    if (renderMode === 'multi') {
      setMultiQueue([...multiQueue, { ...video, tempId: Date.now() + Math.random() }]);
    } else {
      setSelectedVideo(video);
      if(video.duration) setDuration(video.duration);
      if (video.resolution?.includes('x')) {
        const [w, h] = video.resolution.split('x');
        setResW(parseInt(w)); setResH(parseInt(h));
      }
    }
  };

  const formatUrl = (input) => {
    if (!input) return null;
    let path = typeof input === 'object' ? (input.file_url || input.file_path || input.thumbnail || null) : input;
    if (!path) return null;
    if (path.startsWith('http')) return path;
    const cleanPath = path.replace(/^\/?(media\/)?/, '');
    return `https://escreen.stand.ledscreen.rs/media/${cleanPath}`;
  };

  const handleStartProcess = async () => {
    if (!exportName) return message.error("Enter export name");
    setProcessing(true);
    const payload = {
      export_name: exportName, target_bitrate: bitrate, fps: fps,
      media_type: 'video', process_mode: renderMode === 'single' ? 'single' : 'concat',
      parent_media_ids: renderMode === 'single' ? [selectedVideo?.id] : multiQueue.map(v => v.id),
      target_resolution: `${resW}x${resH}`, stack_count: stackCount,
      start_time: `${String(startH).padStart(2,'0')}:${String(startM).padStart(2,'0')}:${String(startS).padStart(2,'0')}`,
      duration: duration, crop_w: cropW, crop_h: cropH, crop_x: cropX, crop_y: cropY
    };
    try {
      await axios.post('/api/box/process-media/', payload);
      message.success("Render started!");
      setExportName('');
      fetchData();
    } catch (e) { message.error("Render failed!"); }
    finally { setProcessing(false); }
  };

  return (
    <div className="mobile-editor-container">
      {/* 1. STATS & UPLOAD */}
      <Row gutter={[8, 8]} style={{ marginBottom: '15px' }}>
        <Col span={12}>
          <div className="st-box" style={{ borderBottomColor: '#3b82f6' }}>
            <Space><FileTextOutlined style={{ color: '#3b82f6' }} /><span style={{ color: 'white', fontWeight: 'bold' }}>{files.length}</span></Space>
          </div>
        </Col>
        <Col span={12}>
          <div className="st-box" style={{ borderBottomColor: activeRenders.length > 0 ? '#3b82f6' : '#22c55e' }}>
            <Space>{activeRenders.length > 0 ? <LoadingOutlined style={{ color: '#3b82f6' }} /> : <CheckCircleOutlined style={{ color: '#22c55e' }} />}<span style={{ color: 'white', fontWeight: 'bold' }}>{activeRenders.length}</span></Space>
          </div>
        </Col>
        <Col span={24}>
          <div className="st-box" style={{ borderBottomColor: '#3b82f6', padding: '0' }}>
            <Upload customRequest={handleUpload} showUploadList={false} disabled={isUploading} style={{ width: '100%' }}>
              <Button block type="dashed" icon={<UploadOutlined />} loading={isUploading} style={{ background: 'transparent', color: '#3b82f6', border: 'none', height: '45px', fontWeight: 'bold' }}>UPLOAD</Button>
            </Upload>
          </div>
          {isUploading && <Progress percent={uploadProgress} size="small" strokeColor="#3b82f6" style={{ marginTop: '5px' }} />}
        </Col>
      </Row>

      {/* 2. SOURCE MEDIA */}
      <div className="m-editor-section" style={{ borderBottomColor: '#64748b' }}>
        <div className="m-section-label">SOURCE MEDIA</div>
        <div className="m-media-scroll">
          {files.map((item) => (
            <div key={item.id} className="m-editor-item" onClick={() => handleSelectMedia(item)}>
               <div className="m-item-thumb">
                 {item.thumbnail ? <img src={formatUrl(item.thumbnail)} alt="" /> : <VideoCameraOutlined style={{ color: '#3b82f6' }} />}
               </div>
               <div style={{ flex: 1, minWidth: 0 }}>
                 <div className="m-item-name">{item.name}</div>
                 <div className="m-item-meta">{item.resolution} | {item.duration}s</div>
               </div>
            </div>
          ))}
        </div>
      </div>

      {/* 3. CONFIGURATION */}
      <div className="m-editor-section" style={{ borderBottomColor: '#fbbf24' }}>
        <div className="m-section-label">CONFIGURATION</div>
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          
          <Row gutter={8}>
            <Col span={16}>
              <label className="m-input-lab">MODE</label>
              <Select value={renderMode} onChange={setRenderMode} onFocus={handleFocus} className="m-dark-select" style={{ width: '100%' }}>
                <Option value="single">SINGLE</Option>
                <Option value="multi">MULTI</Option>
              </Select>
            </Col>
            <Col span={8}>
              <label className="m-input-lab">STACK</label>
              <InputNumber min={1} max={10} value={stackCount} onChange={setStackCount} onFocus={handleFocus} className="m-dark-input" style={{ width: '100%' }} controls={false} />
            </Col>
          </Row>

          {renderMode === 'single' && (
            <>
              <div>
                <label className="m-input-lab">CROP SETTINGS</label>
                <Row gutter={4}>
                  <Col span={6}><div className="tiny-lab">WIDTH</div><InputNumber value={cropW} onChange={setCropW} onFocus={handleFocus} className="m-dark-input" style={{ width: '100%' }} controls={false} /></Col>
                  <Col span={6}><div className="tiny-lab">HEIGHT</div><InputNumber value={cropH} onChange={setCropH} onFocus={handleFocus} className="m-dark-input" style={{ width: '100%' }} controls={false} /></Col>
                  <Col span={6}><div className="tiny-lab">X POS</div><InputNumber value={cropX} onChange={setCropX} onFocus={handleFocus} className="m-dark-input" style={{ width: '100%' }} controls={false} /></Col>
                  <Col span={6}><div className="tiny-lab">Y POS</div><InputNumber value={cropY} onChange={setCropY} onFocus={handleFocus} className="m-dark-input" style={{ width: '100%' }} controls={false} /></Col>
                </Row>
              </div>
              <div>
                <label className="m-input-lab">TIMING & DURATION</label>
                <Row gutter={4}>
                  <Col span={5}><div className="tiny-lab">HOUR</div><InputNumber min={0} value={startH} onChange={setStartH} onFocus={handleFocus} className="m-dark-input" style={{ width: '100%' }} controls={false} /></Col>
                  <Col span={5}><div className="tiny-lab">MIN</div><InputNumber min={0} value={startM} onChange={setStartM} onFocus={handleFocus} className="m-dark-input" style={{ width: '100%' }} controls={false} /></Col>
                  <Col span={5}><div className="tiny-lab">SEC</div><InputNumber min={0} value={startS} onChange={setStartS} onFocus={handleFocus} className="m-dark-input" style={{ width: '100%' }} controls={false} /></Col>
                  <Col span={9}><div className="tiny-lab">DURATION (s)</div><InputNumber min={1} value={duration} onChange={setDuration} onFocus={handleFocus} className="m-dark-input" style={{ width: '100%' }} controls={false} /></Col>
                </Row>
              </div>
            </>
          )}

          <div>
            <label className="m-input-lab">OUTPUT SETTINGS</label>
            <div className="tiny-lab">EXPORT FILENAME</div>
            <Input placeholder="Export Name" value={exportName} onChange={e => setExportName(e.target.value)} onFocus={handleFocus} className="m-dark-input" style={{ marginBottom: '8px' }} />
            <Row gutter={8}>
              <Col span={8}><div className="tiny-lab">RES W</div><InputNumber value={resW} onChange={setResW} onFocus={handleFocus} className="m-dark-input" style={{ width: '100%' }} controls={false} /></Col>
              <Col span={8}><div className="tiny-lab">RES H</div><InputNumber value={resH} onChange={setResH} onFocus={handleFocus} className="m-dark-input" style={{ width: '100%' }} controls={false} /></Col>
              <Col span={8}>
                <div className="tiny-lab">BITRATE</div>
                <Select value={bitrate} onChange={setBitrate} onFocus={handleFocus} className="m-dark-select" style={{ width: '100%' }}>
                  <Option value="2000">2M</Option>
                  <Option value="4000">4M</Option>
                </Select>
              </Col>
            </Row>
            <Row style={{ marginTop: '10px' }}>
              <Col span={24}>
                <div className="tiny-lab">FPS</div>
                <Select value={fps} onChange={setFps} onFocus={handleFocus} className="m-dark-select" style={{ width: '100%' }}>
                  <Option value={24}>24 fps</Option>
                  <Option value={25}>25 fps</Option>
                  <Option value={30}>30 fps</Option>
                  <Option value={50}>50 fps</Option>
                  <Option value={60}>60 fps</Option>
                </Select>
              </Col>
            </Row>
          </div>
          <Button block type="primary" icon={<SaveOutlined />} onClick={handleStartProcess} loading={processing} style={{ height: '45px', background: '#3b82f6', fontWeight: 'bold', marginTop: '10px' }}>START RENDER</Button>
        </Space>
      </div>

      {/* 4. PREVIEW */}
      <div className="m-editor-section" style={{ borderBottomColor: '#3b82f6' }}>
        <div className="m-section-label">PREVIEW / QUEUE</div>
        <div className="m-preview-window">
          {renderMode === 'single' ? (
            selectedVideo ? <video key={selectedVideo.id} controls autoPlay muted src={formatUrl(selectedVideo)} style={{ width: '100%' }} /> : <PlayCircleOutlined style={{ fontSize: '40px', color: '#334155' }} />
          ) : (
            <div style={{ width: '100%' }}>
              <List size="small" dataSource={multiQueue} renderItem={(item, idx) => (
                  <div className="m-queue-item">
                    <span style={{ color: 'white', fontSize: '11px' }}>{idx + 1}. {item.name}</span>
                    <Button type="text" danger icon={<DeleteOutlined />} size="small" onClick={() => setMultiQueue(multiQueue.filter(q => q.tempId !== item.tempId))} />
                  </div>
                )} />
            </div>
          )}
        </div>
      </div>

      <style>{`
        .mobile-editor-container { padding-bottom: 500px; } /* Dodat veliki padding za skrolovanje iznad tastature */
        .st-box { background: #1e293b; padding: 12px; border-radius: 8px; border: 1px solid #334155; border-bottom: 3px solid; display: flex; justify-content: center; align-items: center; }
        .m-editor-section { background: #1e293b; border-radius: 8px; border: 1px solid #334155; border-bottom: 3px solid; padding: 12px; margin-bottom: 15px; }
        .m-section-label { color: white; font-size: 11px; font-weight: bold; margin-bottom: 10px; border-bottom: 1px solid #334155; padding-bottom: 5px; }
        .m-preview-window { background: #0f172a; border-radius: 8px; min-height: 100px; display: flex; align-items: center; justify-content: center; overflow: hidden; }
        .m-queue-item { display: flex; justify-content: space-between; align-items: center; background: #0f172a; padding: 5px 10px; margin-bottom: 4px; border-radius: 4px; }
        
        .m-input-lab { color: #3b82f6; font-size: 11px; font-weight: bold; display: block; margin-bottom: 8px; text-transform: uppercase; }
        .tiny-lab { color: #64748b; font-size: 9px; margin-bottom: 2px; text-transform: uppercase; }
        
        .m-dark-input { background: #0f172a !important; color: white !important; border: 1px solid #334155 !important; }
        .m-dark-select .ant-select-selector { background: #0f172a !important; border: 1px solid #334155 !important; color: white !important; }
        
        .m-media-scroll { max-height: 180px; overflow-y: auto; }
        .m-editor-item { display: flex; gap: 10px; padding: 8px; background: #0f172a; border-radius: 6px; margin-bottom: 6px; align-items: center; border: 1px solid #334155; }
        .m-item-thumb { width: 50px; height: 35px; background: black; border-radius: 4px; overflow: hidden; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
        .m-item-thumb img { width: 100%; height: 100%; object-fit: cover; }
        .m-item-name { color: white; font-size: 12px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .m-item-meta { color: #64748b; font-size: 10px; }
        
        .ant-input-number-handler-wrap { display: none !important; }
      `}</style>
    </div>
  );
};

export default MobileVideoEditor;